import {
  useCallback,
  memo,
  useState,
  useRef,
  ChangeEvent,
  useEffect,
  MouseEvent,
} from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import { Cancel, History, Search, Settings } from "@mui/icons-material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StopIcon from "@mui/icons-material/Stop";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useTranslation } from "react-i18next";
import {
  getVectorSounds,
  getSoundUrl,
  VectorResult,
  getSoundsByIds,
  sendSoundEventLog,
  getSearchSounds,
} from "api-sound-master";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import sendEvent from "modules/sendEvent";
import SoundMasterOptionDialog from "./SoundMasterOptionDialog";
import {
  getFavoriteSounds,
  getSoundsHistories,
} from "stores/modules/entities.user.soundMasterSettings/selectors";
import {
  addFavoriteSound,
  addSoundMasterHistories,
  removeFavoriteSound,
  removeFavoriteSoundByIds,
} from "stores/modules/entities.user.soundMasterSettings/operations";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const SearchContainer = styled.div`
  padding: 16px;
  flex-shrink: 0;
`;

const ResultsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
`;

const TYPES = [
  { value: "instrumental", label: "BGM" },
  { value: "sound_effect", label: "効果音" },
  { value: "voice", label: "ボイス" },
  { value: "vocal", label: "歌" },
];

const LIMIT_FAVORITES_SOUND_MASTER = 1000;
const OTHER_SEARCH_TYPE = ["favorite", "history"];

const MediumListSoundMaster: React.FC = () => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();
  const search = useAppSelector((state) =>
    store.getAppState(state, "soundMasterSearch")
  );
  const selectedType = useAppSelector((state) =>
    store.getAppState(state, "soundMasterSelectedType")
  );
  const limit = useAppSelector((state) =>
    store.getAppState(state, "soundMasterLimit")
  );
  const nuanceSearch = useAppSelector((state) =>
    store.getAppState(state, "nuanceSearch")
  );
  const [searchResults, setSearchResults] = useState<VectorResult[] | null>([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [menuSoundId, setMenuSoundId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<Element | null>(null);
  const [previewingSoundId, setPreviewingSoundId] = useState<string | null>(
    null
  );
  const dir = useAppSelector((state) =>
    store.getAppState(state, "openRoomMediaDir")
  );
  const officialPlaylistVolume = useAppSelector((state) =>
    store.getAppState(state, "officialPlaylistVolume")
  );
  const officialPlaylistLoop = useAppSelector((state) =>
    store.getAppState(state, "officialPlaylistLoop")
  );
  const uid = useAppSelector((state) => store.getAppState(state, "uid"));
  const favoriteSounds = useAppSelector(getFavoriteSounds);
  const soundHistories = useAppSelector(getSoundsHistories);

  const handleToggleFavorite = useCallback(
    async (soundId: string) => {
      if (!uid) return;

      const isFavorite = favoriteSounds.includes(soundId);
      if (isFavorite) {
        dispatch(removeFavoriteSound(uid, soundId));
        // お気に入りタブでお気に入りを解除した場合、表示リストからも削除
        if (selectedType === "favorite") {
          setSearchResults((prev) => {
            if (prev === null) return prev;
            return prev.filter((r) => r.id !== soundId);
          });
        }
      } else {
        // お気に入り上限チェック
        if (favoriteSounds.length >= LIMIT_FAVORITES_SOUND_MASTER) {
          window.alert(
            t(
              `お気に入りは最大${LIMIT_FAVORITES_SOUND_MASTER}件まで登録できます。`
            )
          );
          return;
        }
        dispatch(addFavoriteSound(uid, soundId));
      }
    },
    [uid, favoriteSounds, selectedType, t, dispatch]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        store.appStateMutate((state) => {
          state.soundMasterSearch = e.target.value;
        })
      );
    },
    [dispatch]
  );

  const resetSearch = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        state.soundMasterSearch = "";
      })
    );
    setSearchResults(null);
  }, [dispatch]);

  const loadFavorites = useCallback(async () => {
    if (!uid) return;

    setLoading(true);
    try {
      if (favoriteSounds.length === 0) {
        setSearchResults(null);
        return;
      }

      // お気に入りIDから詳細を取得
      const result = await getSoundsByIds(favoriteSounds);
      // VectorResult型に変換（l2プロパティは0で埋める）
      const vectorResults: VectorResult[] = result.items.map((item) => ({
        ...item,
        l2: 0,
      }));
      const resultMap = new Map<string, VectorResult>(
        vectorResults.map((item) => [item.id, item])
      );
      const sortedResult: VectorResult[] = [];
      favoriteSounds.forEach((id) => {
        const item = resultMap.get(id);
        if (item) sortedResult.push(item);
      });
      setSearchResults(sortedResult);

      // 楽曲情報が取得できなかった楽曲は削除済みのためお気に入りから除外
      if (result.deletedIds.length > 0) {
        dispatch(removeFavoriteSoundByIds(uid, result.deletedIds));
      }
    } catch (err) {
      console.error("Failed to load favorites", err);
      setSearchResults(null);
    } finally {
      setLoading(false);
    }
  }, [uid, favoriteSounds, dispatch]);

  const loadHistories = useCallback(async () => {
    if (!uid) return;

    setLoading(true);
    try {
      if (soundHistories.length === 0) {
        setSearchResults(null);
        return;
      }

      // 履歴IDから詳細を取得
      const result = await getSoundsByIds(soundHistories);
      // VectorResult型に変換（l2プロパティは0で埋める）
      const vectorResults: VectorResult[] = result.items.map((item) => ({
        ...item,
        l2: 0,
      }));
      const resultMap = new Map<string, VectorResult>(
        vectorResults.map((item) => [item.id, item])
      );
      const sortedResult: VectorResult[] = [];
      soundHistories.forEach((id) => {
        const item = resultMap.get(id);
        if (item) sortedResult.push(item);
      });
      setSearchResults(sortedResult);
    } catch (err) {
      console.error("Failed to load histories", err);
      setSearchResults(null);
    } finally {
      setLoading(false);
    }
  }, [uid, soundHistories]);

  const handleSearch = useCallback(
    async (selectWord?: string) => {
      const word = selectWord || search;
      if (!word.trim()) {
        return;
      }

      setSearchResults(null);
      setLoading(true);
      try {
        if (nuanceSearch) {
          const result = await getVectorSounds({
            text: word.trim(),
            type: selectedType,
            isLimitDuration: false,
            size: limit,
          });

          setSearchResults(result.items);
        } else {
          const trimed = word.trim().replace(/　/g, " ");
          const result = await getSearchSounds({
            texts: trimed.split(" "),
            type: selectedType,
            isLimitDuration: false,
            size: limit,
          });

          // l2プロパティを0で埋めてセット
          setSearchResults(
            result.items.map((item) => ({
              ...item,
              l2: 0,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to search sounds", err);
        setSearchResults(null);
      } finally {
        setLoading(false);
      }
    },
    [search, selectedType, limit, nuanceSearch]
  );

  // タブ切り替え時の処理
  useEffect(() => {
    // まず一覧をクリア
    setSearchResults(null);

    if (selectedType === "favorite") {
      // お気に入りタブの場合は自動的にお気に入り一覧を読み込む
      loadFavorites();
    } else if (selectedType === "history") {
      // 履歴タブの場合は自動的に履歴一覧を読み込む
      loadHistories();
    } else if (search.trim()) {
      // 通常タブで検索文字列がある場合は自動的に検索
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const handlePlay = useCallback(
    async (result: VectorResult) => {
      sendEvent("clickSoundMaster", {
        event_category: result.audioTitle,
        event_label: result.id,
      });

      // Send select event with search query as remarks
      sendSoundEventLog("select", result.id, search.trim() || undefined);
      if (uid) await dispatch(addSoundMasterHistories(uid, result.id));

      if (dir === "bgm") {
        dispatch(
          store.updateCurrentRoomField({
            mediaUrl: "",
            mediaRef: result.id,
            mediaType: "file",
            mediaName: result.audioTitle,
            mediaVolume: officialPlaylistVolume,
            mediaRepeat: officialPlaylistLoop,
          })
        );
      } else if (dir === "sound") {
        dispatch(
          store.updateCurrentRoomField({
            soundUrl: "",
            soundRef: result.id,
            soundName: result.audioTitle,
            soundVolume: officialPlaylistVolume,
            soundRepeat: officialPlaylistLoop,
          })
        );
      } else if (dir === "effect") {
        dispatch(
          store.updateCurrentRoomEffect({
            soundUrl: "",
            soundRef: result.id,
            soundName: result.audioTitle,
            soundVolume: officialPlaylistVolume,
          })
        );
      }
      // 試聴を停止
      dispatch(
        store.appStateMutate((state) => {
          state.previewMediaUrl = null;
        })
      );
      setPreviewingSoundId(null);
    },
    [dispatch, dir, officialPlaylistVolume, officialPlaylistLoop, search, uid]
  );

  const handleOpenMenu = useCallback((soundId: string, e: MouseEvent) => {
    setMenuSoundId(soundId);
    setMenuAnchorEl(e.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setTimeout(() => {
      dispatch(
        store.appStateMutate((state) => {
          state.previewMediaUrl = null;
        })
      );
      setPreviewingSoundId(null);
    }, 100);
  }, [dispatch]);

  const handlePreview = useCallback(async () => {
    if (!menuSoundId || searchResults === null) return;

    const result = searchResults.find((r) => r.id === menuSoundId);
    if (!result) return;

    // 現在プレビュー再生中のサウンドかチェック
    const isPreviewing = previewingSoundId === menuSoundId;

    // 視聴停止の場合はすぐに停止（getSoundUrlを呼ばない）
    if (isPreviewing) {
      dispatch(
        store.appStateMutate((state) => {
          state.previewMediaUrl = null;
        })
      );
      setPreviewingSoundId(null);
      return;
    }

    // 視聴開始の場合はURLを取得
    const url = await getSoundUrl(result.id);
    if (!url) return;
    if (uid) await dispatch(addSoundMasterHistories(uid, result.id));

    dispatch(
      store.appStateMutate((state) => {
        state.previewMediaUrl = url;
        state.previewMediaVolume = officialPlaylistVolume;
        state.previewMediaLoop = officialPlaylistLoop;
      })
    );
    setPreviewingSoundId(menuSoundId);
  }, [
    menuSoundId,
    searchResults,
    previewingSoundId,
    dispatch,
    officialPlaylistVolume,
    officialPlaylistLoop,
    uid,
  ]);

  useEffect(() => {
    const ref = searchInputRef.current;
    if (!ref) return;

    if (ref.value !== search) {
      ref.value = search;
    }
  }, [search]);

  return (
    <Container>
      <SearchContainer>
        <Flex>
          <TextField
            inputRef={searchInputRef}
            label={nuanceSearch ? t("あいまい検索") : t("ワード一致検索")}
            fullWidth
            placeholder={
              nuanceSearch
                ? t("楽器、雰囲気、情景などで検索")
                : t("曲名、作曲者名などで検索")
            }
            onChange={onChange}
            disabled={OTHER_SEARCH_TYPE.includes(selectedType)}
            inputProps={{ maxLength: 1000 }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                handleSearch();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    onClick={() => handleSearch()}
                    size="small"
                    disabled={
                      OTHER_SEARCH_TYPE.includes(selectedType) ||
                      loading ||
                      !search.trim()
                    }
                    edge="start"
                  >
                    <Search />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {search && (
                    <IconButton
                      onClick={resetSearch}
                      size="small"
                      disabled={OTHER_SEARCH_TYPE.includes(selectedType)}
                    >
                      <Cancel />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={() => setFilterDialogOpen(true)}
            size="small"
            edge="end"
            disabled={OTHER_SEARCH_TYPE.includes(selectedType)}
          >
            <Settings />
          </IconButton>
        </Flex>
        <Box mt={2} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={selectedType}
            onChange={(_, newValue) => {
              dispatch(
                store.appStateMutate((state) => {
                  state.soundMasterSelectedType = newValue;
                })
              );
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TYPES.map((type) => (
              <StyledTab
                key={type.value}
                label={type.label}
                value={type.value}
                disabled={loading}
              />
            ))}
            <StyledTab
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <StarIcon fontSize="small" />
                  {t("お気に入り")}
                </Box>
              }
              value="favorite"
              disabled={loading}
            />
            <StyledTab
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <History fontSize="small" />
                  {t("履歴")}
                </Box>
              }
              value="history"
              disabled={loading}
            />
          </Tabs>
        </Box>
      </SearchContainer>
      <SoundMasterOptionDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
      />

      <ResultsContainer>
        {OTHER_SEARCH_TYPE.includes(selectedType) &&
        loading &&
        (searchResults === null || searchResults.length === 0) ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : OTHER_SEARCH_TYPE.includes(selectedType) &&
          (searchResults === null || searchResults.length === 0) ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              {selectedType === "favorite"
                ? t("お気に入りに登録されたサウンドがありません")
                : t("選択、試聴履歴がありません")}
            </Typography>
          </Box>
        ) : loading &&
          (searchResults === null || searchResults.length === 0) ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : searchResults && searchResults.length > 0 ? (
          <>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {searchResults.length}
              {selectedType === "favorite"
                ? t("件のお気に入り") +
                  ` (${favoriteSounds.length}/${LIMIT_FAVORITES_SOUND_MASTER})`
                : t("件の結果")}
            </Typography>
            <List>
              {searchResults.map((result) => {
                const duration = result.audioDuration;
                const minutes = Math.floor(duration / 60);
                const seconds = Math.floor(duration % 60);
                const durationText = `${minutes}:${seconds
                  .toString()
                  .padStart(2, "0")}`;

                const isFavorite = favoriteSounds.includes(result.id);

                return (
                  <ListItem
                    key={result.id}
                    disablePadding
                    secondaryAction={
                      <Box display="flex" gap={0.5}>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(result.id);
                          }}
                          color={isFavorite ? "warning" : "default"}
                        >
                          {isFavorite ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenMenu(result.id, e);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemButton
                      onClick={() => handlePlay(result)}
                      style={{ paddingRight: "72px" }}
                    >
                      <ListItemText
                        primary={
                          <>
                            {result.audioTitle}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              {durationText}
                            </Typography>
                          </>
                        }
                        secondary={
                          <>
                            {result.audioShortDescription ||
                              result.audioFullDescription}
                            {result.youtubeSafety && (
                              <Chip
                                label="YouTube安心"
                                size="small"
                                sx={{
                                  height: "18px",
                                  fontSize: "0.6rem",
                                  bgcolor: "action.hover",
                                  color: "text.secondary",
                                  ml: 1,
                                }}
                              />
                            )}
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        ) : !loading && !OTHER_SEARCH_TYPE.includes(selectedType) ? (
          <Box textAlign="center" py={4} px={2}>
            {nuanceSearch ? (
              <>
                <Typography color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                  {t("シーンの雰囲気を入力して音源を探してみましょう")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {t("🎵 検索例:")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {t("・「森の中で怪物と戦闘」")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {t("・「のんびりした午後の喫茶店」")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {t("・「緊迫した潜入シーン」")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  {t(
                    "物語の描写やニュアンスから、近い雰囲気の音源を見つけられます"
                  )}
                </Typography>
              </>
            ) : searchResults === null ? (
              <Typography color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {t("お気に入りの楽曲や作曲者から音源を探しましょう")}
              </Typography>
            ) : (
              <Typography color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {t("検索結果が見つかりませんでした")}
              </Typography>
            )}
          </Box>
        ) : null}
      </ResultsContainer>

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handlePreview}>
          <ListItemIcon>
            {previewingSoundId === menuSoundId ? (
              <StopIcon />
            ) : (
              <PlayArrowIcon />
            )}
          </ListItemIcon>
          <ListItemText style={{ minWidth: "72px" }}>
            {previewingSoundId === menuSoundId ? t("試聴停止") : t("試聴")}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Container>
  );
};

export const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledTab = styled(Tab)`
  min-width: 50px;
`;

export default memo(MediumListSoundMaster);
